'use client';

import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from 'next/navigation';
import { useState, type ChangeEvent } from 'react';
import { Controller, FormProvider, useForm, type FieldPath, type PathValue, type Resolver } from 'react-hook-form';
import { toast } from 'react-toastify';
import { clientTabs } from '@/app/(main)/client-detail/consts/client-detail';
import { useCreateAirRequestMutation } from '@/app/(main)/client-detail/hooks/useCreateAirRequestMutation';
import { primaryContactActions } from '@/app/(main)/client-detail/utils/contacts';
import { DuplicateClientWarningModal } from '@/app/(main)/clients/components/DuplicateClientWarningModal';
import { QUICK_ADD_CLIENT_SOURCE_OPTIONS } from '@/app/(main)/clients/const/quickAddClientSources';
import { useCheckDuplicateClientsMutation } from '@/app/(main)/clients/hooks/useCheckDuplicateClientsMutation';
import { buildCheckDuplicateClientsPayload } from '@/app/(main)/clients/utils/buildCheckDuplicateClientsPayload';
import { buildClientFullName } from '@/app/(main)/clients/utils/buildClientFullName';
import Checkbox from '@/app/components/Checkbox';
import { Modal } from '@/app/components/Modal';
import { BaseButton } from '@/app/components/buttons/BaseButton';
import { Input } from '@/app/components/form/Input';
import { PhoneInput } from '@/app/components/form/PhoneInput';
import { Selector } from '@/app/components/form/Selector';
import type { SelectorOption } from '@/app/components/form/Selector/types';
import { SingleDatePicker } from '@/app/components/form/SingleDatePicker';
import { CLIENT_ID_QUERY_KEY } from '@/consts/const';
import { GENDER_OPTIONS } from '@/consts/options';
import { CLIENT_DETAIL_INTERNAL_PATH } from '@/consts/paths';
import { getAirRequestPath } from '@/consts/paths-utils';
import { useUser } from '@/context/UserProvider';
import { useFetch } from '@/hooks/useFetch';
import { useIndexedDbDraft } from '@/hooks/useIndexedDbDraft';
import { useSearchQuery } from '@/hooks/useSearchQuery';
import type { ErrorResponse } from '@/types/apiTypes';
import type { CreateClientResponse, DuplicateClientMatch, EditClientData, GetClientResponse } from '@/types/clientsApiTypes';
import type { GenderType } from '@/types/common';
import { formatMMDDYYYYToYYYYMMDD } from '@/utils/formatDate';
import { setBEFieldError } from '@/utils/setBEFieldError';
import { QUICK_ADD_CLIENT_ID_QUERY_KEY } from '../../const/clients';
import { useCreateClientMutation } from '../../create/hooks/useCreateClientMutation';
import { useEditClientMutation } from '../../hooks/useEditClientMutation';
import { usePublishClientMutation } from '../../hooks/usePublishClientMutation';
import { getQuickAddClientValidationSchema, type QuickAddClientFormValues } from './utils/getValidationSchema';
import ArrowBackIcon from '/public/icons/arrow-back.svg';

export type QuickAddClientModalMode = 'default' | 'air-request';

export const QUICK_ADD_CLIENT_DEFAULT_TITLE = 'Quick add client';
export const QUICK_ADD_CLIENT_DEFAULT_DESCRIPTION =
  'Create a new client profile with essential information to get started.';

const SUBMIT_LABELS: Record<QuickAddClientModalMode, { idle: string; pending: string }> = {
  default: { idle: 'Save', pending: 'Saving…' },
  'air-request': { idle: 'Continue to air request', pending: 'Continuing…' },
};

interface QuickAddClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode?: QuickAddClientModalMode;
  title?: string;
  description?: string;
}

const validationSchema = getQuickAddClientValidationSchema();

const parseQuickAddClientId = (value: string | null): number | null => {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const AIR_REQUEST_DRAFT_DB_NAME = 'air-request-drafts';

const defaultFormValues: QuickAddClientFormValues = {
  userHasCertifiedTheInformation: false,
  first_name: '',
  last_name: '',
  middle_name: '',
  preferred_name: '',
  email: '',
  phone: '',
  date_of_birth: '',
  gender: '',
  source: null,
};

export const QuickAddClientModal = ({
  isOpen,
  onClose,
  mode = 'default',
  title = QUICK_ADD_CLIENT_DEFAULT_TITLE,
  description = QUICK_ADD_CLIENT_DEFAULT_DESCRIPTION,
}: QuickAddClientModalProps) => {
  const router = useRouter();
  const [searchQuery, updateSearchQuery] = useSearchQuery();
  const { mutateAsync: createDraftClient, isPending: isCreating } = useCreateClientMutation();
  const { doFetch: getClientDetails } = useFetch<undefined, GetClientResponse>();
  const { mutateAsync: editClient, isPending: isSaving } = useEditClientMutation();
  const { mutateAsync: publishClient, isPending: isPublishing } = usePublishClientMutation();
  const { mutateAsync: createAirRequest, isPending: isCreatingAirRequest } = useCreateAirRequestMutation();
  const { mutateAsync: checkDuplicateClients, isPending: isCheckingDuplicates } = useCheckDuplicateClientsMutation();
  const { user } = useUser();
  const quickAddClientIdParam = searchQuery.get(QUICK_ADD_CLIENT_ID_QUERY_KEY);
  const isAirRequestMode = mode === 'air-request';
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [duplicateMatches, setDuplicateMatches] = useState<DuplicateClientMatch[] | null>(null);
  const [pendingSubmitData, setPendingSubmitData] = useState<QuickAddClientFormValues | null>(null);
  const [duplicateSearchValues, setDuplicateSearchValues] = useState({ email: '', fullName: '' });

  const airRequestDraftCache = useIndexedDbDraft<{ draftClientId: number }>({
    key: user?.id ? `air-request-draft-${user.id}` : 'air-request-draft-anonymous',
    dbName: AIR_REQUEST_DRAFT_DB_NAME,
  });
  const canCacheDraft = isAirRequestMode && Boolean(user?.id);

  const methods = useForm<QuickAddClientFormValues>({
    resolver: yupResolver(validationSchema) as Resolver<QuickAddClientFormValues>,
    defaultValues: defaultFormValues,
  });

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = methods;

  const registerWithShouldValidate = <N extends FieldPath<QuickAddClientFormValues>>(name: N) => {
    const registration = register(name);
    return {
      ...registration,
      onChange: (event: ChangeEvent<HTMLInputElement>) => {
        void registration.onChange(event);
        setValue(name, event.target.value as PathValue<QuickAddClientFormValues, N>, { shouldValidate: true });
      },
    };
  };

  const isCertified = watch('userHasCertifiedTheInformation');
  const gender = watch('gender');
  const source = watch('source');

  const createClient = async (data: QuickAddClientFormValues) => {
    let clientId = parseQuickAddClientId(quickAddClientIdParam);

    if (!clientId && canCacheDraft) {
      const cached = await airRequestDraftCache.restore().catch(() => null);
      if (cached?.draftClientId) {
        clientId = cached.draftClientId;
      }
    }

    const resumed = Boolean(clientId);
    if (!clientId) {
      const createResponse: CreateClientResponse = await createDraftClient();
      const newId = createResponse?.data?.id;
      clientId = newId;
      updateSearchQuery({ [QUICK_ADD_CLIENT_ID_QUERY_KEY]: String(newId) });
      if (canCacheDraft) {
        await airRequestDraftCache.save({ draftClientId: newId }).catch(() => null);
      }
    }

    const stored = resumed ? await getClientDetails(`/api/client-details/${clientId}`) : null;
    const dateOfBirth = data.date_of_birth ? (formatMMDDYYYYToYYYYMMDD(data.date_of_birth) ?? null) : null;

    const payload: Partial<EditClientData> = {
      client: {
        first_name: data.first_name.trim(),
        last_name: data.last_name.trim(),
        middle_name: data.middle_name?.trim() ? data.middle_name.trim() : null,
        preferred_name: data.preferred_name?.trim() ? data.preferred_name.trim() : null,
        ...primaryContactActions(data.email, data.phone, stored?.data?.emails ?? [], stored?.data?.phones ?? []),
        date_of_birth: dateOfBirth,
        gender: data.gender?.trim() ? data.gender.trim() : null,
        userHasCertifiedTheInformation: data.userHasCertifiedTheInformation,
        source_id: data.source ? Number(data.source) : null,
      },
    };

    await editClient({ data: payload, clientId });

    await publishClient(clientId);

    if (isAirRequestMode) {
      const airRequestResponse = await createAirRequest({ clientId });
      const airRequestId = airRequestResponse?.data?.id;

      if (!airRequestId) {
        throw new Error('Air request response did not include an id');
      }

      if (canCacheDraft) {
        await airRequestDraftCache.clear().catch(() => null);
      }

      updateSearchQuery({ [QUICK_ADD_CLIENT_ID_QUERY_KEY]: null });

      setIsRedirecting(true);
      toast.success('Client created. Continuing to air request…');
      onClose();
      router.push(getAirRequestPath(String(airRequestId), String(clientId)));
      return;
    }

    updateSearchQuery({ [QUICK_ADD_CLIENT_ID_QUERY_KEY]: null });

    setIsRedirecting(true);
    toast.success('Client created successfully');
    onClose();
    router.push(`${CLIENT_DETAIL_INTERNAL_PATH}?${CLIENT_ID_QUERY_KEY}=${clientId}&tab=${clientTabs.main}`);
  };

  const resolveExistingDraftId = async (): Promise<number | undefined> => {
    const paramDraftId = parseQuickAddClientId(quickAddClientIdParam);
    if (paramDraftId) {
      return paramDraftId;
    }
    if (canCacheDraft) {
      const cached = await airRequestDraftCache.restore().catch(() => null);
      if (cached?.draftClientId) {
        return cached.draftClientId;
      }
    }
    return undefined;
  };

  const onSubmit = async (data: QuickAddClientFormValues) => {
    try {
      const fullName = buildClientFullName(data.first_name, data.middle_name, data.last_name);
      const email = data.email.trim();
      const excludeClientId = await resolveExistingDraftId();
      const duplicateResponse = await checkDuplicateClients(
        buildCheckDuplicateClientsPayload({
          first_name: data.first_name,
          last_name: data.last_name,
          email,
          exclude_client_id: excludeClientId,
        })
      );

      if (duplicateResponse.has_duplicates) {
        setDuplicateSearchValues({ email, fullName });
        setDuplicateMatches(duplicateResponse.duplicates);
        setPendingSubmitData(data);
        return;
      }

      await createClient(data);
    } catch (error) {
      setBEFieldError(error as ErrorResponse<unknown>, setError);
      toast.error((error as ErrorResponse<unknown>)?.message ?? 'Something went wrong');
    }
  };

  const handleDuplicateModalClose = () => {
    setDuplicateMatches(null);
    setPendingSubmitData(null);
  };

  const handleContinueAnyway = async () => {
    if (!pendingSubmitData) {
      return;
    }

    const data = pendingSubmitData;
    setDuplicateMatches(null);
    setPendingSubmitData(null);

    try {
      await createClient(data);
    } catch (error) {
      setBEFieldError(error as ErrorResponse<unknown>, setError);
      toast.error((error as ErrorResponse<unknown>)?.message ?? 'Something went wrong');
    }
  };

  const busy = isCreating || isSaving || isPublishing || isCreatingAirRequest || isRedirecting || isCheckingDuplicates;
  const submitLabel = busy ? SUBMIT_LABELS[mode].pending : SUBMIT_LABELS[mode].idle;

  return (
    <>
      <Modal open={isOpen} onClose={onClose} variant="fullScreenMobile">
        <div className="flex flex-col overflow-hidden max-md:h-full max-md:w-full md:h-[min(730px,85dvh)] md:w-[600px] md:rounded-3xl">
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
              <div className="relative flex flex-col gap-2 px-4.5 pt-8 text-center bg-white shrink-0 max-md:gap-5 md:px-9 md:pt-11.5">
                <div className="relative flex items-center justify-center">
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label="Go back"
                    className="md:hidden absolute left-0 flex items-center cursor-pointer"
                  >
                    <ArrowBackIcon viewBox="0 0 18 18" className="w-4 h-4 text-blue-300" aria-hidden />
                  </button>
                  <h2 className="light text-blue-900 max-md:text-lg max-md:leading-[22px] max-md:tracking-[0.006em]">
                    {title}
                  </h2>
                </div>
                <p className="text-body-regular text-grey-700 max-md:text-left max-md:text-sm max-md:leading-[150%]">
                  {description}
                </p>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-4 px-4.5 pt-5 pb-5 md:px-9">
                <div className="w-full">
                  <Checkbox
                    name="userHasCertifiedTheInformation"
                    checked={!!isCertified}
                    size="medium"
                    variant="transparent"
                    labelClassName="!text-blue-900 max-md:!text-black max-md:!text-sm max-md:!leading-[150%]"
                    onChange={(event) => {
                      setValue('userHasCertifiedTheInformation', event.target.checked, { shouldValidate: true });
                    }}
                    label="I certify the information below matches the information that appears on the passenger’s government-issued ID"
                  />
                </div>

                <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                  <Input
                    className="max-md:col-span-2"
                    label="First name"
                    placeholder="Enter first name"
                    required
                    {...registerWithShouldValidate('first_name')}
                    errors={errors}
                  />
                  <Input
                    className="max-md:col-span-2"
                    label="Last name"
                    placeholder="Enter last name"
                    required
                    {...registerWithShouldValidate('last_name')}
                    errors={errors}
                  />
                  <Input
                    className="max-md:col-span-2"
                    label="Middle name"
                    placeholder="Enter middle name"
                    {...registerWithShouldValidate('middle_name')}
                    errors={errors}
                  />
                  <Input
                    className="max-md:col-span-2"
                    label="Preferred name"
                    placeholder="Enter preferred name"
                    {...registerWithShouldValidate('preferred_name')}
                    errors={errors}
                  />
                  <Input
                    className="max-md:col-span-2"
                    label="Primary email"
                    placeholder="example@smartflyer.com"
                    {...registerWithShouldValidate('email')}
                    errors={errors}
                  />
                  <PhoneInput<QuickAddClientFormValues, 'phone'>
                    className="max-md:col-span-2"
                    name="phone"
                    label="Primary phone number"
                    placeholder="+1 (555) 000-0000"
                    value={watch('phone') ?? ''}
                    errors={errors}
                    setError={setError}
                    clearErrors={clearErrors}
                    onChange={(value) => {
                      setValue('phone', value?.phoneWithoutMask ?? '', { shouldValidate: true });
                    }}
                    disableDialCodePrefill
                  />
                  <Controller
                    name="date_of_birth"
                    control={control}
                    render={({ field }) => (
                      <SingleDatePicker
                        name="date_of_birth"
                        label="Date of birth"
                        placeholder="MM/DD/YYYY"
                        errors={errors}
                        value={field.value || ''}
                        maxDate={new Date()}
                        onBlur={field.onBlur}
                        onChangeDate={(date) => {
                          setValue('date_of_birth', date ?? '', { shouldValidate: true });
                        }}
                      />
                    )}
                  />
                  <Selector
                    className="w-full"
                    options={GENDER_OPTIONS}
                    value={gender || ''}
                    label="Gender"
                    onChange={(option) => {
                      const next = (option as SelectorOption<GenderType>).value ?? '';
                      setValue('gender', next, { shouldValidate: true });
                    }}
                    placeholder="Select gender"
                    menuPosition="fixed"
                    maxMenuHeight={220}
                    errors={errors}
                    name="gender"
                  />

                  <Selector
                    className="w-full col-span-2"
                    options={QUICK_ADD_CLIENT_SOURCE_OPTIONS}
                    value={Number(source)}
                    label="Source"
                    required={false}
                    onChange={(option) => {
                      const next = (option as SelectorOption<number>)?.value ?? null;
                      setValue('source', next ?? null, { shouldValidate: true });
                    }}
                    placeholder="Select source"
                    menuPosition="fixed"
                    maxMenuHeight={220}
                    errors={errors}
                    name="source"
                  />
                </div>
              </div>

              <div className="mt-2 flex w-full items-center gap-4 bg-white shrink-0 sticky bottom-0 pb-8 px-4.5 md:justify-between md:gap-5 md:pb-11.5 md:px-9">
                <BaseButton
                  type="button"
                  intent="secondary"
                  size="full"
                  className="w-full max-w-[250px] max-md:max-w-none max-md:flex-1"
                  onClick={onClose}
                  disabled={busy}
                >
                  Cancel
                </BaseButton>
                <BaseButton
                  type="submit"
                  intent="primary"
                  size="full"
                  className="w-full max-w-[250px] max-md:max-w-none max-md:flex-1"
                  disabled={busy || !methods.formState.isValid}
                >
                  {submitLabel}
                </BaseButton>
              </div>
            </form>
          </FormProvider>
        </div>
      </Modal>

      {duplicateMatches && (
        <DuplicateClientWarningModal
          isOpen={Boolean(duplicateMatches)}
          matches={duplicateMatches}
          searchedEmail={duplicateSearchValues.email}
          searchedFullName={duplicateSearchValues.fullName}
          isContinueLoading={isCreating || isSaving || isPublishing || isCreatingAirRequest || isRedirecting}
          onClose={handleDuplicateModalClose}
          onContinue={handleContinueAnyway}
        />
      )}
    </>
  );
};
